import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Eye, Award, Music, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { settingsApi } from '../lib/api';

const AboutPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await settingsApi.getAbout();
        setContent(res.data);
      } catch (error) {
        console.error('Error fetching about content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="about-page">
      <SEO 
        title="Quiénes Somos | BRAVO PRODUCCIONES"
        description={content?.subtitle || "Conoce más sobre BRAVO PRODUCCIONES, la productora de eventos más importante de la región."}
        url="/quienes-somos"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-4">
              {content?.title || 'Quiénes Somos'}
            </h1>
            <p className="text-lg sm:text-xl text-[#D4AF37] max-w-3xl mx-auto">
              {content?.subtitle || 'La productora de eventos más importante de la región'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 border-t border-[#27272A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-lg sm:text-xl text-[#A1A1AA] leading-relaxed">
              {content?.description || 'Somos BRAVO PRODUCCIONES, una empresa dedicada a crear experiencias únicas e inolvidables.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      {content?.stats && content.stats.length > 0 && (
        <section className="py-16 bg-[#121212] border-y border-[#27272A]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {content.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#D4AF37] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm sm:text-base text-[#A1A1AA] uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* History Section */}
      {content?.history && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-6 flex items-center gap-3">
                <Calendar className="text-[#D4AF37]" />
                Nuestra Historia
              </h2>
              <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-line">
                {content.history}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Mission & Vision */}
      <section className="py-16 bg-[#121212] border-y border-[#27272A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            {content?.mission && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-[#0A0A0A] border border-[#27272A] p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
                    <Target className="text-[#D4AF37]" size={24} />
                  </div>
                  <h3 className="text-xl font-black uppercase">Misión</h3>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {content.mission}
                </p>
              </motion.div>
            )}

            {/* Vision */}
            {content?.vision && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-[#0A0A0A] border border-[#27272A] p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
                    <Eye className="text-[#D4AF37]" size={24} />
                  </div>
                  <h3 className="text-xl font-black uppercase">Visión</h3>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {content.vision}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {content?.team && content.team.length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-12 text-center flex items-center justify-center gap-3"
            >
              <Users className="text-[#D4AF37]" />
              Nuestro Equipo
            </motion.h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#121212] border border-[#27272A] p-6 text-center group hover:border-[#D4AF37]/50 transition-colors"
                >
                  {member.image && (
                    <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full border-2 border-[#27272A] group-hover:border-[#D4AF37] transition-colors">
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <h4 className="font-bold text-lg mb-1">{member.name}</h4>
                  <p className="text-[#D4AF37] text-sm uppercase tracking-wider mb-2">{member.role}</p>
                  {member.bio && (
                    <p className="text-[#A1A1AA] text-sm">{member.bio}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default AboutPage;
